import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import useToast from '@sdk/common/toast/useToast';
import getErrorForToast from '@sdk/api/utils/getErrorForToast';
import useSlingApi from '@sdk/api/useSlingApi';
import gettext from '@sdk/lang/gettext';
import { ModalState } from '@sdk/components/ui/modal/useModalState';

import { ACTION_TOAST_ID } from '@web/conversations/Conversation/ConversationHeaderActions/conversationHeaderActions.consts';
import { useConversationState } from '@web/conversations/Conversation/ConversationProvider/ConversationProvider';
import { downloadFile } from '@web/common/utils/downloadFile';

type ConversationExportFormValues = {
  mimetype: string;
  startDate: Date;
  endDate: Date;
};

const defaultValues = {
  mimetype: '',
  startDate: null,
  endDate: null,
};

type Props = {
  modalState: ModalState;
};
export const useExportConversationModal = ({ modalState }: Props) => {
  const { showSuccessToast, showErrorToast } = useToast();
  const { conversation } = useSlingApi();
  const { id, name } = useConversationState();

  const formMethods = useForm<ConversationExportFormValues>({ defaultValues });
  const { control, watch, reset } = formMethods;

  const onSubmit = formMethods.handleSubmit(async (submitData: ConversationExportFormValues) => {
    try {
      const response = await conversation.export(id, submitData);
      const fileName = `Sling-conversation-with-${name.replaceAll(' ', '-')}`;
      downloadFile(response.data, fileName);

      modalState.close();
      showSuccessToast('Your export is complete and can be found in your downloads', {
        containerId: ACTION_TOAST_ID,
      });
    } catch (e) {
      showErrorToast(getErrorForToast(e, gettext('Your conversation could not be exported.')), {
        containerId: ACTION_TOAST_ID,
      });
    }
  });

  const startDateValue = watch('startDate');
  const endDateValue = watch('endDate');

  useEffect(() => {
    if (!modalState.isVisible) {
      reset(defaultValues);
    }
  }, [modalState.isVisible]);

  return { formMethods, onSubmit, control, startDateValue, endDateValue };
};
